/**
 * Customers Domain API Route Definitions
 *
 * Zod-typed route definitions for the Customers CRM API.
 */

import { z } from "zod";

import {
  AddNoteBodySchema,
  BulkTagBodySchema,
  CreateCustomerBodySchema,
  CustomerAddNoteResponseSchema,
  CustomerAnalyticsQuerySchema,
  CustomerAnalyticsResponseSchema,
  CustomerBulkTagResponseSchema,
  CustomerCreateResponseSchema,
  CustomerDeleteNoteResponseSchema,
  CustomerDeleteResponseSchema,
  CustomerDetailWrappedResponseSchema,
  CustomerFieldSettingsResponseSchema,
  CustomerImportResponseSchema,
  CustomerLinkSessionResponseSchema,
  CustomerListResponseSchema,
  CustomerMergeResponseSchema,
  CustomerPathVarsSchema,
  CustomerPickerOptionsQuerySchema,
  CustomerPickerOptionsResponseSchema,
  CustomerRelatedResponseSchema,
  CustomerSearchMessagesQuerySchema,
  CustomerSearchMessagesResponseSchema,
  CustomerTimelineQuerySchema,
  CustomerTimelineResponseSchema,
  CustomerUpdateNoteResponseSchema,
  CustomerUpdateResponseSchema,
  CustomerUploadNoteImageResponseSchema,
  LinkSessionBodySchema,
  ListCustomersQuerySchema,
  MergeCustomerBodySchema,
  NotePathVarsSchema,
  UnlinkedSessionsQuerySchema,
  UnlinkedSessionsResponseSchema,
  UpdateCustomerBodySchema,
  UpdateCustomerFieldSettingsBodySchema,
  UpdateNoteBodySchema
} from "./schemas";

/**
 * Both upload routes below are `multipart/form-data` — the backend reads them
 * with `FileInterceptor("file")` + `@UploadedFile()`. A Zod object cannot
 * describe a multipart payload, so the body is declared as an opaque `FormData`
 * exactly as `ZDocumentTemplateFolders.UpdateIcon` does. That declaration is
 * load-bearing: the generator emits `data: opts.body` ONLY for an endpoint that
 * declares a `Body`, so omitting it produces a mutation that posts nothing at
 * all — which is what the hand-maintained client was silently compensating for.
 *
 * Content-Type is deliberately not set here. axios derives
 * `multipart/form-data` WITH its boundary from a FormData instance; a hand-set
 * header carries no boundary.
 */
const FormDataBodySchema = z.custom<FormData>();

export const ZCustomers = {
  List: {
    method: "GET" as const,
    path: "/api/customers",
    Query: ListCustomersQuerySchema,
    Response: CustomerListResponseSchema
  },
  Create: {
    method: "POST" as const,
    path: "/api/customers",
    Body: CreateCustomerBodySchema,
    Response: CustomerCreateResponseSchema
  },
  Get: {
    method: "GET" as const,
    path: "/api/customers/:id",
    PathVars: CustomerPathVarsSchema,
    Response: CustomerDetailWrappedResponseSchema
  },
  Update: {
    method: "PATCH" as const,
    path: "/api/customers/:id",
    PathVars: CustomerPathVarsSchema,
    Body: UpdateCustomerBodySchema,
    Response: CustomerUpdateResponseSchema
  },
  Delete: {
    method: "DELETE" as const,
    path: "/api/customers/:id",
    PathVars: CustomerPathVarsSchema,
    Response: CustomerDeleteResponseSchema
  },
  Merge: {
    method: "POST" as const,
    path: "/api/customers/:id/merge",
    PathVars: CustomerPathVarsSchema,
    Body: MergeCustomerBodySchema,
    Response: CustomerMergeResponseSchema
  },
  AddNote: {
    method: "POST" as const,
    path: "/api/customers/:id/notes",
    PathVars: CustomerPathVarsSchema,
    Body: AddNoteBodySchema,
    Response: CustomerAddNoteResponseSchema
  },
  UpdateNote: {
    method: "PATCH" as const,
    path: "/api/customers/:id/notes/:noteId",
    PathVars: NotePathVarsSchema,
    Body: UpdateNoteBodySchema,
    Response: CustomerUpdateNoteResponseSchema
  },
  DeleteNote: {
    method: "DELETE" as const,
    path: "/api/customers/:id/notes/:noteId",
    PathVars: NotePathVarsSchema,
    Response: CustomerDeleteNoteResponseSchema
  },
  BulkTag: {
    method: "POST" as const,
    path: "/api/customers/bulk/tags",
    Body: BulkTagBodySchema,
    Response: CustomerBulkTagResponseSchema
  },
  /**
   * GET /api/customers/export
   *
   * Streams a CSV attachment through `@Res()` — the handler returns
   * `Promise<void>` and writes the response itself, so there is no JSON
   * envelope to validate. `responseType: "blob"` is the codebase idiom for
   * that (see `ZDashboard.ExportDashboardData`), and it is what stops codegen
   * emitting a `.parse()` the stream would never satisfy.
   */
  Export: {
    method: "GET" as const,
    path: "/api/customers/export",
    Query: ListCustomersQuerySchema,
    responseType: "blob" as const
  },
  Import: {
    method: "POST" as const,
    path: "/api/customers/import",
    Body: FormDataBodySchema,
    Response: CustomerImportResponseSchema
  },
  Timeline: {
    method: "GET" as const,
    path: "/api/customers/:id/timeline",
    PathVars: CustomerPathVarsSchema,
    Query: CustomerTimelineQuerySchema,
    Response: CustomerTimelineResponseSchema
  },
  SearchMessages: {
    method: "GET" as const,
    path: "/api/customers/:id/messages/search",
    PathVars: CustomerPathVarsSchema,
    Query: CustomerSearchMessagesQuerySchema,
    Response: CustomerSearchMessagesResponseSchema
  },
  Related: {
    method: "GET" as const,
    path: "/api/customers/:id/related",
    PathVars: CustomerPathVarsSchema,
    Response: CustomerRelatedResponseSchema
  },
  LinkSession: {
    method: "POST" as const,
    path: "/api/customers/:id/link-session",
    PathVars: CustomerPathVarsSchema,
    Body: LinkSessionBodySchema,
    Response: CustomerLinkSessionResponseSchema
  },
  UnlinkedSessions: {
    method: "GET" as const,
    path: "/api/customers/unlinked-sessions",
    Query: UnlinkedSessionsQuerySchema,
    Response: UnlinkedSessionsResponseSchema
  },
  /**
   * Lightweight picker endpoint — returns only the 5 fields a filter
   * dropdown / mention list needs (id, displayName, email, phone,
   * avatarUrl). Powers both the keystroke-search and the
   * by-ids-hydration paths in `CustomerFilter` and any future picker
   * variant. Distinct from `List` (which returns the full Customer
   * payload including customFields blobs) — using `List` for picker
   * surfaces would over-fetch and silently drop selections that fall
   * outside its default lastSeenAt window.
   */
  PickerOptions: {
    method: "GET" as const,
    path: "/api/customers/picker-options",
    Query: CustomerPickerOptionsQuerySchema,
    Response: CustomerPickerOptionsResponseSchema
  },
  GetFieldSettings: {
    method: "GET" as const,
    path: "/api/customers/settings/fields",
    Response: CustomerFieldSettingsResponseSchema
  },
  UpdateFieldSettings: {
    method: "PUT" as const,
    path: "/api/customers/settings/fields",
    Body: UpdateCustomerFieldSettingsBodySchema,
    Response: CustomerFieldSettingsResponseSchema
  },
  Analytics: {
    method: "GET" as const,
    path: "/api/customers/:id/analytics",
    PathVars: CustomerPathVarsSchema,
    Query: CustomerAnalyticsQuerySchema,
    Response: CustomerAnalyticsResponseSchema
  },
  UploadNoteImage: {
    method: "POST" as const,
    path: "/api/customers/:id/notes/upload-image",
    PathVars: CustomerPathVarsSchema,
    Body: FormDataBodySchema,
    Response: CustomerUploadNoteImageResponseSchema
  }
} as const;
